import React, { useState, useRef, useEffect } from 'react';
import styles from './CorrectionButton.module.css';
import { CorrectionForm } from './CorrectionForm';
import type { CorrectionFormData } from './CorrectionForm';

export interface CorrectionButtonProps {
  messageId: string;
  messageContent: string;
  skill?: string;
  visible?: boolean;
  onSubmit: (messageId: string, data: CorrectionFormData) => void;
}

export const CorrectionButton: React.FC<CorrectionButtonProps> = ({
  messageId,
  messageContent,
  skill,
  visible = false,
  onSubmit,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSubmit = (data: CorrectionFormData) => {
    onSubmit(messageId, data);
    setIsOpen(false);
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        className={`${styles.button} ${visible || isOpen ? styles.visible : ''}`.trim()}
        onClick={() => setIsOpen((prev) => !prev)}
        type="button"
        aria-label="Correct this response"
      >
        <span className={styles.icon}>&#9998;</span>
        Correct this
      </button>

      {isOpen && (
        <div className={styles.formContainer}>
          <div className={styles.formHeader}>Submit Correction</div>
          <CorrectionForm
            originalValue={messageContent}
            messageId={messageId}
            skill={skill}
            onSubmit={handleSubmit}
            onCancel={() => setIsOpen(false)}
          />
        </div>
      )}
    </div>
  );
};
